import React, { useEffect, useMemo, useState } from 'react'
import PageHeader from '../../../components/PageHeader/PageHeader'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material'
import RuleIcon from '@mui/icons-material/Rule'
import AddIcon from '@mui/icons-material/Add'
import moment from 'moment'
import API from '../../../services/API'
import MFALogin from '../../../helpers/auth/MFALogin'
import useIsMobile from '../../../helpers/useIsMobile'
import Loading from '../../loading/loadingPage'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import NumberCardTwo from '../../../components/Cards/NumberCardTwo'
import EmptyState from '../../../components/EmptyState/EmptyState'
import Age from '../../../components/Age'
import AuditDialog from './AuditDialog'
import RaiseAuditDialog from './RaiseAuditDialog'

// Secondary columns hidden on phones (still reachable via the grid's column picker)
const MOBILE_HIDDEN_COLUMNS = {
  date: false,
  clinician: false,
  reasons: false,
  flagged: false,
  auditedBy: false,
  completedAt: false,
}

// NumberCardTwo fixes its number box at 25% width, which is too narrow for
// values like "50%" on a half-width phone card — let it size to content on xs.
const STAT_CARD_SX = {
  paddingX: { xs: 1.5, md: 2 },
  '& > div:first-of-type': {
    width: { xs: 'auto', md: '25%' },
    fontSize: { xs: '1.5rem', md: '2rem' },
    flexShrink: 0,
  },
  '& > div:last-of-type': {
    fontSize: { xs: '0.875rem', md: '1rem' },
    minWidth: 0,
  },
}

const EpcrAudits = () => {
  const isMobile = useIsMobile()
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({})
  const [loading, setLoading] = useState(true)
  const [requiresMFA, setRequiresMFA] = useState(false)
  const [fetchError, setFetchError] = useState(false)
  const [preventSelfAudit, setPreventSelfAudit] = useState(true)
  const [audits, setAudits] = useState([])
  const [tab, setTab] = useState('Required')
  const [selectedAudit, setSelectedAudit] = useState(null)
  const [raiseOpen, setRaiseOpen] = useState(false)

  // `openAuditID` opens that audit once loaded, e.g. one just raised by hand
  const loadAudits = (openAuditID) => {
    API.get('/epcr-audits')
      .then((res) => {
        if (res.status === 200) {
          const nextAudits = res.data.audits ?? []
          setAudits(nextAudits)
          // Keep an open dialog in step with the reloaded audit
          setSelectedAudit((prev) => {
            const id = typeof openAuditID === 'string' ? openAuditID : prev?._id
            return id
              ? nextAudits.find((audit) => audit._id === id) ?? null
              : prev
          })
          setPreventSelfAudit(res.data.preventSelfAudit ?? true)
          setRequiresMFA(false)
        } else if (res.status === 403) {
          setRequiresMFA(true)
        } else {
          setFetchError(true)
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadAudits()
  }, [])

  useEffect(() => {
    setColumnVisibilityModel(isMobile ? MOBILE_HIDDEN_COLUMNS : {})
  }, [isMobile])

  const outstanding = useMemo(
    () => audits.filter((audit) => audit.status === 'Required'),
    [audits],
  )
  const completedAudits = useMemo(
    () => audits.filter((audit) => audit.status === 'Completed'),
    [audits],
  )
  const completedThisMonth = completedAudits.filter((audit) =>
    moment(audit.completedAt).isSame(moment(), 'month'),
  ).length
  const compliantCount = completedAudits.filter(
    (audit) => audit.overallCompliant,
  ).length

  const rows = tab === 'Required' ? outstanding : completedAudits

  const columns = useMemo(() => {
    const base = [
      {
        field: 'epcrNumber',
        headerName: isMobile ? 'ePCR' : 'ePCR No',
        minWidth: isMobile ? 84 : 100,
        valueGetter: (value, row) => row.epcr?.epcrNumber ?? '',
      },
      {
        field: 'date',
        headerName: 'Incident Date',
        minWidth: 140,
        valueGetter: (value, row) =>
          moment(row.epcr?.incident?.incidentDate ?? row.epcr?.createdAt).unix(),
        renderCell: ({ row }) =>
          moment(row.epcr?.incident?.incidentDate ?? row.epcr?.createdAt).format(
            'DD/MM/YYYY',
          ),
      },
      {
        field: 'patient',
        headerName: 'Patient',
        minWidth: isMobile ? 130 : 180,
        flex: 1,
        valueGetter: (value, row) => {
          const demographics = row.epcr?.demographics
          if (!demographics?.firstName && !demographics?.lastName) return ''
          const age = demographics?.dob
            ? ` (${Age(demographics.dob, row.epcr?.createdAt)})`
            : ''
          return `${demographics?.lastName ?? ''}, ${
            demographics?.firstName?.[0] ?? ''
          }${age}`
        },
      },
      {
        field: 'clinician',
        headerName: 'Clinician',
        minWidth: 130,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) =>
          row.clinician
            ? `${row.clinician.firstName} ${row.clinician.lastName}`
            : '',
        renderCell: ({ row }) =>
          row.clinician ? (
            <Box
              display="flex"
              height="100%"
              width="100%"
              alignItems="center"
              justifyContent="center"
            >
              <Tooltip
                title={`${row.clinician.firstName} ${row.clinician.lastName}${
                  row.auditedClinician ? ' (chosen by Governance)' : ''
                }`}
              >
                <Avatar
                  sx={{ width: 28, height: 28, borderRadius: 1 }}
                  src={row.clinician.photograph}
                />
              </Tooltip>
            </Box>
          ) : (
            ''
          ),
      },
      {
        field: 'reasons',
        headerName: 'Audit Reasons',
        minWidth: 240,
        flex: 1,
        valueGetter: (value, row) => (row.reasons || []).join(', '),
        renderCell: ({ row }) => (
          <Stack
            direction="row"
            gap={0.5}
            alignItems="center"
            height="100%"
            flexWrap="wrap"
          >
            {(row.reasons || []).map((reason) => (
              <Chip
                key={reason}
                label={reason}
                size="small"
                color="anPurple"
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Stack>
        ),
      },
    ]

    if (tab === 'Required') {
      return [
        ...base,
        {
          field: 'flagged',
          headerName: 'Flagged',
          minWidth: 130,
          valueGetter: (value, row) => moment(row.createdAt).unix(),
          renderCell: ({ row }) => moment(row.createdAt).fromNow(),
        },
        {
          field: 'involvement',
          headerName: 'Eligibility',
          minWidth: isMobile ? 140 : 150,
          renderCell: ({ row }) =>
            row.viewerInvolved ? (
              <Box display="flex" height="100%" alignItems="center">
                <Tooltip
                  title={
                    preventSelfAudit
                      ? 'You were involved in this record, so another member of the Governance team must audit it.'
                      : 'You were involved in this record. Where possible it should be audited by someone else.'
                  }
                >
                  <Chip
                    label="You're involved"
                    size="small"
                    color={preventSelfAudit ? 'error' : 'warning'}
                    sx={{ borderRadius: 1 }}
                  />
                </Tooltip>
              </Box>
            ) : (
              ''
            ),
        },
      ]
    }

    return [
      ...base,
      {
        field: 'outcome',
        headerName: 'Outcome',
        minWidth: 140,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (value, row) =>
          row.overallCompliant ? 'Compliant' : 'Not Compliant',
        renderCell: ({ row }) => (
          <Chip
            label={row.overallCompliant ? 'Compliant' : 'Not Compliant'}
            size="small"
            color={row.overallCompliant ? 'success' : 'error'}
            sx={{ borderRadius: 1 }}
          />
        ),
      },
      {
        field: 'auditedBy',
        headerName: 'Audited By',
        minWidth: 150,
        valueGetter: (value, row) =>
          row.completedBy
            ? `${row.completedBy.firstName} ${row.completedBy.lastName}`
            : '',
      },
      {
        field: 'completedAt',
        headerName: 'Completed',
        minWidth: 130,
        valueGetter: (value, row) => moment(row.completedAt).unix(),
        renderCell: ({ row }) => moment(row.completedAt).format('DD/MM/YYYY'),
      },
    ]
  }, [tab, preventSelfAudit, isMobile])

  if (loading) return <Loading />

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          gap={1}
          flexWrap="wrap"
        >
          <PageHeader />
          {!requiresMFA && !fetchError && (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setRaiseOpen(true)}
            >
              Raise Audit
            </Button>
          )}
        </Stack>
        {requiresMFA && <MFALogin onSuccess={loadAudits} />}
      </Grid>

      {fetchError && (
        <Grid item xs={12}>
          <Alert severity="error">Unable to load record audits</Alert>
        </Grid>
      )}

      {!requiresMFA && !fetchError && (
        <>
          <Grid item xs={12}>
            <Grid container spacing={1}>
              <NumberCardTwo
                number={outstanding.length}
                label="Awaiting Audit"
                color={outstanding.length > 0 ? 1 : 4}
                tooltipTitle="Submitted records flagged for audit that have not been audited yet"
                {...STAT_CARD_SX}
              />
              <NumberCardTwo
                number={completedThisMonth}
                label="Completed This Month"
                color={3}
                tooltipTitle="Audits completed this calendar month"
                {...STAT_CARD_SX}
              />
              <NumberCardTwo
                number={
                  completedAudits.length
                    ? `${Math.round(
                        (compliantCount / completedAudits.length) * 100,
                      )}%`
                    : '—'
                }
                label="Compliance Rate"
                color={9}
                tooltipTitle="Completed audits where the record met every criterion"
                {...STAT_CARD_SX}
              />
              <NumberCardTwo
                number={completedAudits.length - compliantCount}
                label="Not Compliant"
                color={completedAudits.length - compliantCount > 0 ? 10 : 9}
                tooltipTitle="Completed audits where the record fell short on one or more criteria"
                {...STAT_CARD_SX}
              />
            </Grid>
          </Grid>

          {audits.length === 0 ? (
            <Grid item xs={12}>
              <EmptyState
                icon={RuleIcon}
                title="No records flagged for audit yet"
                description="When an ePCR is submitted it is checked against your audit criteria - records that match appear here for the Governance team to review."
                steps={[
                  'Open Site Configuration → Patient Records',
                  'Choose which records should be audited',
                  'Submitted records matching your criteria will be listed here',
                ]}
                actions={[
                  {
                    label: 'Configure Audit Criteria',
                    to: '/site-configuration',
                  },
                ]}
                sx={{ boxSizing: 'border-box' }}
              />
            </Grid>
          ) : (
            <>
              <Grid item xs={12}>
                <Tabs
                  value={tab}
                  onChange={(e, value) => setTab(value)}
                  variant="scrollable"
                  scrollButtons="auto"
                  allowScrollButtonsMobile
                >
                  <Tab
                    label={`Awaiting Audit (${outstanding.length})`}
                    value="Required"
                  />
                  <Tab
                    label={`Completed (${completedAudits.length})`}
                    value="Completed"
                  />
                </Tabs>
              </Grid>
              <Grid item xs={12}>
                <CustomDataGrid
                  rows={rows}
                  columns={columns}
                  getRowId={(row) => row._id}
                  maxHeight={600}
                  hideFilters
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityModelChange={setColumnVisibilityModel}
                  disableRowSelectionOnClick
                  onRowClick={({ row }) => setSelectedAudit(row)}
                  slots={{
                    noRowsOverlay: () => (
                      <Box
                        width="100%"
                        height="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Alert severity="success">
                          {tab === 'Required'
                            ? 'No records awaiting audit'
                            : 'No completed audits yet'}
                        </Alert>
                      </Box>
                    ),
                  }}
                  sx={{
                    '.MuiDataGrid-cell:focus': {
                      outline: 'none',
                    },
                    '& .MuiDataGrid-row:hover': {
                      cursor: 'pointer',
                    },
                  }}
                />
              </Grid>
            </>
          )}
        </>
      )}

      <AuditDialog
        audit={selectedAudit}
        preventSelfAudit={preventSelfAudit}
        onClose={() => setSelectedAudit(null)}
        onCompleted={() => {
          setSelectedAudit(null)
          loadAudits()
        }}
        onClinicianChanged={loadAudits}
      />
      <RaiseAuditDialog
        open={raiseOpen}
        onClose={() => setRaiseOpen(false)}
        onRaised={(auditID) => {
          setRaiseOpen(false)
          setTab('Required')
          loadAudits(auditID)
        }}
      />
    </Grid>
  )
}

export default EpcrAudits
